import React from 'react'
import { Link } from 'react-router'
import { ExternalLink, FileText, Link2, Printer, Route, Share2 } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import Menu, { MenuItem } from '../ui/Menu'

/** Outlined page-header button (DESIGN.md → button-secondary). */
export const SECONDARY_BTN =
  'inline-flex h-[38px] items-center gap-2 rounded-[7px] border border-rule-strong bg-paper px-3.5 text-sm font-semibold text-ink hover:border-ink-300 transition-colors whitespace-nowrap'

export const ICON_BTN =
  'inline-flex items-center justify-center rounded-md text-ink-600 hover:bg-rule-soft hover:text-ink transition-colors'

/** Copy text to the clipboard with a toast either way. */
export function useCopyWithToast() {
  const { showToast } = useToast()
  return async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showToast(successMessage)
    } catch {
      showToast('Kunne ikke kopiere til udklipsholder.', { tone: 'signal' })
    }
  }
}

interface ProfileActionLinksProps {
  shareUrl: string
  /** Source citation (name, id, source, read date, link) for »Kopiér kildehenvisning«. */
  citation: string
  datacvrHref: string
  datacvrAriaLabel: string
  /** »Find forbindelse« with this entity filled in. */
  connectionHref?: string
  /** Primary action shown last (e.g. the watchlist button). */
  children?: React.ReactNode
}

/**
 * Button row on the right of company and person headers: a share menu
 * (link, source citation, PDF), open on CVR.dk, then the primary action.
 */
const ProfileActionLinks: React.FC<ProfileActionLinksProps> = ({
  shareUrl,
  citation,
  datacvrHref,
  datacvrAriaLabel,
  connectionHref,
  children,
}) => {
  const copyText = useCopyWithToast()
  return (
    <div className="flex flex-wrap items-center gap-2 print:hidden" role="group" aria-label="Del og handlinger">
      <Menu
        align="right"
        trigger={<Share2 className="h-[17px] w-[17px]" aria-hidden />}
        triggerAriaLabel="Del"
        triggerClassName={`${ICON_BTN} h-[38px] w-[38px] border border-rule-strong bg-paper`}
      >
        {(close) => (
          <>
            <MenuItem
              icon={<Link2 className="h-3.5 w-3.5" />}
              onClick={() => {
                close()
                void copyText(shareUrl, 'Link kopieret.')
              }}
            >
              Kopiér link til denne side
            </MenuItem>
            <MenuItem
              icon={<FileText className="h-3.5 w-3.5" />}
              onClick={() => {
                close()
                void copyText(citation, 'Kildehenvisning kopieret.')
              }}
            >
              Kopiér kildehenvisning
            </MenuItem>
            <MenuItem
              icon={<Printer className="h-3.5 w-3.5" />}
              onClick={() => {
                close()
                window.print()
              }}
            >
              Gem som PDF / udskriv
            </MenuItem>
          </>
        )}
      </Menu>
      {connectionHref && (
        <Link to={connectionHref} className={SECONDARY_BTN}>
          <Route className="h-4 w-4 text-ink-500" aria-hidden />
          Find forbindelse
        </Link>
      )}
      <a
        href={datacvrHref}
        target="_blank"
        rel="noopener noreferrer"
        className={SECONDARY_BTN}
        aria-label={datacvrAriaLabel}
        title="Åbn i CVR-registeret (cvr.dk)"
      >
        CVR.dk
        <ExternalLink className="h-4 w-4 text-ink-500" aria-hidden />
      </a>
      {children}
    </div>
  )
}

/** Shown only on paper: where the printed page came from. */
export const PrintCitation: React.FC<{ citation: string }> = ({ citation }) => (
  <p className="hidden print:block mt-8 border-t border-rule pt-3 text-xs text-ink-500">
    {citation}
  </p>
)

export default ProfileActionLinks
